import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { areaForPath, isFullHeightRoute } from './navRegistry';
import { PhoneLayoutProvider } from '@xcentral/ui';
import { HelpProvider } from '../help/HelpProvider';
import { useHelp } from '../help/helpContext';

// Lazy + mounted only while open, so react-markdown (needed only once help is opened)
// stays out of the always-loaded app-shell bundle.
const HelpDrawer = lazy(() => import('../components/HelpDrawer').then(m => ({ default: m.HelpDrawer })));

function HelpDrawerMount() {
  const { isOpen } = useHelp();
  if (!isOpen) return null;
  return (
    <Suspense fallback={null}>
      <HelpDrawer />
    </Suspense>
  );
}

/**
 * Story #22190 — the three shell layouts (prototype #p-phone-patterns, BR-MOB-001 / BR-MOB-010):
 * - `phone`   (< 768px)       — no area strip in the top bar; the drawer carries the areas.
 * - `tablet`  (768 – 1023px)  — area strip in the top bar; the sidebar is an OVERLAY drawer.
 * - `desktop` (>= 1024px)     — unchanged: strip in the top bar, sidebar docked.
 *
 * Breakpoints are Tailwind's `md` (768) and `lg` (1024). jsdom implements no `matchMedia`, so the
 * fallback is `desktop` — every existing test that renders the shell keeps today's layout, and the
 * tests that cover the narrow layouts mock `matchMedia` (same posture as `DetailPeek`).
 */
export type ShellViewport = 'phone' | 'tablet' | 'desktop';

const MD_QUERY = '(min-width: 768px)';
const LG_QUERY = '(min-width: 1024px)';

function readViewport(): ShellViewport {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
  if (window.matchMedia(LG_QUERY).matches) return 'desktop';
  return window.matchMedia(MD_QUERY).matches ? 'tablet' : 'phone';
}

function useShellViewport(): ShellViewport {
  const [viewport, setViewport] = useState<ShellViewport>(readViewport);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const queries = [window.matchMedia(MD_QUERY), window.matchMedia(LG_QUERY)];
    const update = () => setViewport(readViewport());
    update();
    queries.forEach(q => q.addEventListener('change', update));
    return () => queries.forEach(q => q.removeEventListener('change', update));
  }, []);
  return viewport;
}

export function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const viewport = useShellViewport();
  // BR-MOB-010 — docked only at 1024px and up; below that the sidebar is an overlay drawer,
  // closed by default and opened by the hamburger.
  const sidebarMode = viewport === 'desktop' ? 'docked' : 'overlay';

  // Growing past 1024px docks the sidebar, so an open overlay (and its backdrop) must not linger.
  useEffect(() => {
    if (sidebarMode === 'docked') setMobileOpen(false);
  }, [sidebarMode]);

  // Escape closes the overlay drawer, like every other overlay in the app.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  // #14378 — the selected nav area. Derived from the current route (so deep-linking and browser
  // back/forward select the right tab + submenu — BR-NAV-003), but held in state so a tab click
  // can also select an area that has no route yet (Procurement, whose screens are still to come).
  // The route always wins on navigation, so the two can't drift apart.
  const { pathname } = useLocation();
  const routeAreaId = areaForPath(pathname);
  const fullHeight = isFullHeightRoute(pathname);
  const [activeAreaId, setActiveAreaId] = useState(routeAreaId);
  useEffect(() => { setActiveAreaId(routeAreaId); }, [routeAreaId]);

  // Story #22197 — the admin portal opts in to the shared phone patterns (BR-MOB-006/007/008/011/012).
  // The provider defaults OFF, so the client portal keeps today's rendering until its own prototype
  // defines phone patterns (#22239).
  return (
    <PhoneLayoutProvider>
    <HelpProvider>
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Mobile overlay — button so screen readers + keyboard users can dismiss the sidebar */}
      {mobileOpen && sidebarMode === 'overlay' && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        mode={sidebarMode}
        showAreaGrid={viewport === 'phone'}
        activeAreaId={activeAreaId}
        onSelectArea={setActiveAreaId}
        onCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main content */}
      {/* `min-w-0` lets this column shrink to the viewport instead of to its widest child, so the
          content area itself never scrolls sideways at 375px (BR-MOB-008 / BR-MOB-009). */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar
          viewport={viewport}
          onMenuClick={() => setMobileOpen(true)}
          activeAreaId={activeAreaId}
          onSelectArea={setActiveAreaId}
        />
        {/* #13185 — the app-wide `max-w-7xl` cap this wrapper used to apply is removed
            so list screens (DataListShell — Table/toolbar/pager) span the full available
            content width, per the "page width is wasted" mockup feedback. Pages that want
            a narrower reading width already carry their own inner `max-w-*` wrapper
            (AccountCreate, ContactList, agreement/onboarding forms, etc.) and are
            unaffected. */}
        {/* Story #17760 (BR-HUB-V-127) — a full-height route owns its own height and its own
            scrolling; every other route keeps the single scroller byte-for-byte.

            Inside `overflow-y-auto` a child cannot be "the remaining height": it grows, the page
            scrolls, and the tab strip / filter bar / answer buttons leave the viewport. On a screen
            you ANSWER from — the Hub Inbox — that means scrolling past the question to reach the
            verbs. `overflow-hidden` hands the height to the page, which then declares its own
            scroll regions (the inbox declares two: the list and the preview body).

            The page also takes over PADDING, because `p-6` on a flex parent eats the height the
            panes are trying to fill. That is why this is one branch and not a class toggle.

            Story #22190 (BR-MOB-008) — side gutters are 16px below 768px (`p-4`) and the existing
            24px (`md:p-6`) from 768px up, so tablet and desktop are unchanged.

            Story #22197 (BR-MOB-011) — both <main> elements are the named size container `main`
            (`@container/main`), so content grids respond to the width left after the sidebar, not the
            viewport: `@md/main:grid-cols-2` etc. A container only gains inline-size containment; it
            does not change this element's own layout. */}
        {fullHeight ? (
          <main className="@container/main flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </main>
        ) : (
          <main className="@container/main min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
        )}
      </div>
    </div>
    <HelpDrawerMount />
    </HelpProvider>
    </PhoneLayoutProvider>
  );
}
